import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refsTradeEquipment, type TradeEquipment } from '@/api/refsTradeEquipment';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'refs.trade_equipment.manage';

const TYPE_LABEL: Record<string, string> = {
  scale: 'Ваги',
  scanner: 'Сканер',
  printer: 'Принтер',
  cash_drawer: 'Грошова скринька',
  other: 'Інше',
};

export default function TradeEquipmentPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['refs-trade-equipment', orgId],
    queryFn: () =>
      refsTradeEquipment.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<TradeEquipment>[]>(
    () => [
      { key: 'code', header: 'Код', width: 130, sortValue: (r) => r.code ?? '', cell: (r) => r.code },
      { key: 'name', header: 'Назва', sortValue: (r) => r.name ?? '', cell: (r) => r.name },
      {
        key: 'equipment_type',
        header: 'Тип',
        width: 160,
        sortValue: (r) => TYPE_LABEL[r.equipment_type] ?? r.equipment_type,
        cell: (r) => TYPE_LABEL[r.equipment_type] ?? r.equipment_type,
      },
      { key: 'serial_number', header: 'Серійний №', width: 160, sortValue: (r) => r.serial_number ?? '', cell: (r) => r.serial_number },
      {
        key: 'is_active',
        header: 'Активний',
        width: 110,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓' : '—'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав керувати торгівельним обладнанням (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Торгівельне обладнання
          <HintBadge
            title="Що таке торгівельне обладнання?"
            text={
              'Довідник пристроїв, підключених до POS-станції: ваги, сканери штрих-кодів, ' +
              'чек-принтери, грошові скриньки.\n\n' +
              'Обладнання можна прив\'язати до конкретної POS-станції (тоді при роботі касир ' +
              'бачить тільки своє обладнання) або залишити «вільним» (наприклад склад-ваги).\n\n' +
              'Серійний номер — для інвентаризації. Якщо потрібно вимкнути обладнання тимчасово — ' +
              'зніміть прапорець «Активний» (без видалення з довідника).'
            }
          />
        </h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 140, flex: '0 0 auto' }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={canCreate ? undefined : tooltipNew}
          onClick={() => navigate(`/retail/trade-equipment/new?org=${orgId}`)}
        >
          + Обладнання
        </button>
      </div>
      <DataTable<TradeEquipment>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає обладнання."
        onRowDoubleClick={(r) => navigate(`/retail/trade-equipment/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="name"
      />
    </>
  );
}
